import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useState as useHookState } from 'react';

export interface RacerData {
  racerId: string;
  email: string;
  registrationName: string;
  fullName: string;
  motherName: string;
  birthPlace: string;
  birthDate: string;
  address: string;
  phoneNumber: string;
  isProfileComplete: boolean;
}

interface RacerRegistrationProps {
  userId: string;
  itemCount: number;
  onComplete: (racers: RacerData[]) => void;
  onCancel: () => void;
  isAdmin: boolean;
}

const RacerRegistration: React.FC<RacerRegistrationProps> = ({
  userId,
  itemCount,
  onComplete,
  onCancel,
  isAdmin
}) => {
  const [racers, setRacers] = useState<RacerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentRacerIndex, setCurrentRacerIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<RacerData[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Inicializáljuk a vendégek tömbjét a kívánt számú gokarttal
  useEffect(() => {
    const initializeRacers = async () => {
      setLoading(true);
      try {
        const racersArray: RacerData[] = [];

        // Minden vendég (beleértve az elsőt is) üres adatokkal kezd
        for (let i = 0; i < itemCount; i++) {
          racersArray.push({
            racerId: `R${Math.floor(100000 + Math.random() * 900000)}`,
            email: '',
            registrationName: '',
            fullName: '',
            motherName: '',
            birthPlace: '',
            birthDate: '',
            address: '',
            phoneNumber: '',
            isProfileComplete: false
          });
        }

        setRacers(racersArray);
      } catch (error) {
        console.error('Hiba a vendégek inicializálása során:', error);
        setError('Nem sikerült betölteni a vendégek adatait. Kérjük, próbáld újra később.');
      } finally {
        setLoading(false);
      }
    };

    initializeRacers();
  }, [userId, itemCount]);

  // Módosítjuk a profilok betöltését
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const profilesRef = collection(db, 'userProfiles');

        if (!isAdmin) {
          // A dokumentum ID-ja maga a userID
          const userProfileDoc = doc(profilesRef, userId);
          const userProfileSnapshot = await getDoc(userProfileDoc);

          if (userProfileSnapshot.exists()) {
            const userProfileData = userProfileSnapshot.data() as RacerData;
            // Automatikusan kitöltjük az első vendég adatait, megtartva az eredeti racerId-t
            setRacers(prevRacers => {
              const updatedRacers = [...prevRacers];
              updatedRacers[0] = {
                ...userProfileData,
                // Ha van racerId a profilban, azt használjuk, különben marad az eredeti
                racerId: userProfileData.racerId || prevRacers[0].racerId,
                isProfileComplete: true
              };
              return updatedRacers;
            });
          }
          setProfiles([]); // Nem adminnak nincs szüksége a profilok listájára
        } else {
          // Admin esetén az összes profilt lekérjük
          const profilesSnapshot = await getDocs(profilesRef);
          const profilesData = profilesSnapshot.docs
            .map(doc => ({
              ...doc.data() as RacerData,
              userId: doc.id
            }))
            .filter(profile => profile.isProfileComplete)
            .sort((a, b) => a.fullName.localeCompare(b.fullName, 'hu'));
          setProfiles(profilesData);
        }
      } catch (error) {
        console.error('Hiba a profilok betöltése során:', error);
      } finally {
        setProfilesLoading(false);
      }
    };

    fetchProfiles();
  }, [userId, isAdmin]);

  // Vendég adatainak frissítése
  const handleRacerChange = (index: number, field: keyof RacerData, value: string) => {
    setRacers(prevRacers => {
      const updatedRacers = [...prevRacers];
      const isSelfProfile = index === 0;
      updatedRacers[index] = {
        ...updatedRacers[index],
        [field]: value,
        isProfileComplete: field === 'isProfileComplete'
          ? value === 'true'
          : checkProfileComplete({
            ...updatedRacers[index],
            [field]: value
          }, isSelfProfile)
      };
      return updatedRacers;
    });
  };

  // Ellenőrizzük, hogy minden kötelező mező ki van-e töltve
  const checkProfileComplete = (racer: RacerData, isSelfProfile: boolean): boolean => {
    // Saját profil esetén minden mező kötelező
    if (isSelfProfile) {
      return (
        racer.fullName.trim() !== '' &&
        racer.motherName.trim() !== '' &&
        racer.address.trim() !== '' &&
        racer.phoneNumber.trim() !== '' &&
        racer.email.trim() !== '' &&
        racer.birthPlace.trim() !== '' &&
        racer.birthDate.trim() !== ''
      );
    }

    // Más vendégeknél csak a név kötelező
    return racer.fullName.trim() !== '';
  };

  // Vendég kiválasztása a keresési eredményekből
  const selectRacer = (racer: RacerData) => {
    setRacers(prevRacers => {
      const updatedRacers = [...prevRacers];
      updatedRacers[currentRacerIndex] = racer;
      return updatedRacers;
    });
  };

  // Következő vendégre lépés
  const goToNextRacer = () => {
    if (currentRacerIndex < itemCount - 1) {
      setCurrentRacerIndex(currentRacerIndex + 1);
    } else {
      // Ha az utolsó vendégnél vagyunk, akkor mentjük az adatokat
      handleSaveAll();
    }
  };

  // Előző vendégre lépés
  const goToPreviousRacer = () => {
    if (currentRacerIndex > 0) {
      setCurrentRacerIndex(currentRacerIndex - 1);
    }
  };

  // Minden vendég adatainak mentése
  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);

    try {
      // Ellenőrizzük a vendégek adatait
      const isValid = racers.every((racer, index) => {
        const isSelfProfile = index === 0;
        return checkProfileComplete(racer, isSelfProfile);
      });

      if (!isValid) {
        setError('A saját adatoknál minden mező kitöltése kötelező, más vendégeknél csak a név megadása szükséges.');
        setSaving(false);
        return;
      }

      // Ellenőrizzük és mentjük a vendégek adatait
      const savePromises = racers.map(async (racer, index) => {
        const isSelfProfile = index === 0;

        // Ellenőrizzük mindkét kollekcióban
        const userProfilesQuery = query(
          collection(db, 'userProfiles'),
          where('fullName', '==', racer.fullName),
          where('motherName', '==', racer.motherName),
          where('birthDate', '==', racer.birthDate),
          where('address', '==', racer.address)
        );

        const racersQuery = query(
          collection(db, 'racers'),
          where('fullName', '==', racer.fullName),
          where('motherName', '==', racer.motherName),
          where('birthDate', '==', racer.birthDate),
          where('address', '==', racer.address)
        );

        const [userProfilesSnapshot, racersSnapshot] = await Promise.all([
          getDocs(userProfilesQuery),
          getDocs(racersQuery)
        ]);

        // Ellenőrizzük, hogy teljesen ki van-e töltve a profil
        const isFullyComplete = (
          racer.fullName.trim() !== '' &&
          racer.motherName.trim() !== '' &&
          racer.address.trim() !== '' &&
          racer.phoneNumber.trim() !== '' &&
          racer.email.trim() !== '' &&
          racer.birthPlace.trim() !== '' &&
          racer.birthDate.trim() !== ''
        );

        const promises = [];

        // Ha még nem szerepel a racers kollekcióban és legalább a név ki van töltve
        if (racersSnapshot.empty && racer.fullName.trim() !== '') {
          const racersRef = collection(db, 'racers');
          promises.push(
            setDoc(doc(racersRef, racer.racerId), {
              ...racer,
              userId: userId,
              createdAt: new Date(),
              updatedAt: new Date(),
              isProfileComplete: isFullyComplete
            })
          );
        }

        // Ha teljesen ki van töltve és még nem szerepel a userProfiles kollekcióban
        if (isFullyComplete && userProfilesSnapshot.empty) {
          const userProfilesRef = collection(db, 'userProfiles');
          promises.push(
            setDoc(doc(userProfilesRef), {
              ...racer,
              userId: userId,
              createdAt: new Date(),
              updatedAt: new Date(),
              isProfileComplete: true
            })
          );
        }

        // Várjuk meg mindkét mentést
        await Promise.all(promises);
      });

      await Promise.all(savePromises);

      // Visszaadjuk a vendégek adatait
      onComplete(racers);
    } catch (error) {
      console.error('Hiba a vendégek mentése során:', error);
      setError('Nem sikerült menteni a vendégek adatait. Kérjük, próbáld újra később.');
    } finally {
      setSaving(false);
    }
  };

  // Segédfüggvény a dátum formázásához
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Új függvény a vendég ID generálásához
  const generateRacerId = () => `R${Math.floor(100000 + Math.random() * 900000)}`;

  // Új függvény a mezők törléséhez
  const handleClearFields = () => {
    setRacers(prevRacers => {
      const updatedRacers = [...prevRacers];
      updatedRacers[currentRacerIndex] = {
        racerId: generateRacerId(), // Új ID generálása
        email: '',
        registrationName: '',
        fullName: '',
        motherName: '',
        birthPlace: '',
        birthDate: '',
        address: '',
        phoneNumber: '',
        isProfileComplete: false
      };
      return updatedRacers;
    });
  };

  // A renderProfileSelector módosítása
  const renderProfileSelector = () => {
    // Ha nem admin, akkor ne jelenjen meg a kereső
    if (!isAdmin) {
      return null;
    }

    if (profilesLoading) {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Válassz egy meglévő profilt
          </label>
          <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
            Profilok betöltése...
          </div>
        </div>
      );
    }

    const availableProfiles = profiles.filter(profile =>
      !racers.some((racer, index) =>
        index !== currentRacerIndex &&
        (racer.racerId === profile.racerId ||
          (racer.fullName === profile.fullName &&
            racer.birthDate === profile.birthDate &&
            racer.address === profile.address))
      )
    );

    const filteredProfiles = availableProfiles.filter(profile =>
      profile.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.birthDate?.includes(searchTerm) ||
      profile.address?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (availableProfiles.length === 0) {
      return (
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Nincs több elérhető profil. Kérjük, add meg manuálisan az adatokat.
          </p>
        </div>
      );
    }

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Válassz egy meglévő profilt
        </label>
        <div className="relative">
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
            placeholder="Keress név, születési idő vagy lakcím alapján..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={(e) => {
              setTimeout(() => {
                setIsOpen(false);
              }, 200);
            }}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 px-3 flex items-center"
            onClick={() => setIsOpen(!isOpen)}
          >
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {isOpen && filteredProfiles.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-300">
              {filteredProfiles.map((profile) => (
                <button
                  key={profile.racerId}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  onClick={() => {
                    selectRacer(profile);
                    setSearchTerm('');
                    setIsOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{profile.fullName}</span>
                    <span className="text-sm text-gray-600">
                      {[
                        profile.birthDate && `Születési idő: ${profile.birthDate}`,
                        profile.address && `Lakcím: ${profile.address}`
                      ].filter(Boolean).join(' • ')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {searchTerm && filteredProfiles.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">
            Nincs találat a keresésre. Próbáld más keresési feltétellel vagy add meg manuálisan az adatokat.
          </p>
        )}
      </div>
    );
  };

  // Az aktuális vendég adatlapja
  const renderRacerForm = () => {
    const racer = racers[currentRacerIndex];
    const isSelfProfile = currentRacerIndex === 0;

    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {`${currentRacerIndex + 1}. vendég adatai`}
          </h3>
          <button
            type="button"
            onClick={handleClearFields}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border border-gray-300"
          >
            Mezők törlése
          </button>
        </div>

        {renderProfileSelector()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendég ID
            </label>
            <input
              type="text"
              value={racer.racerId}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email cím{isSelfProfile && ' *'}
            </label>
            <input
              type="email"
              value={racer.email}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.email
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required={isSelfProfile}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teljes név *
            </label>
            <input
              type="text"
              value={racer.fullName}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'fullName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${!racer.fullName
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anyja neve{isSelfProfile && ' *'}
            </label>
            <input
              type="text"
              value={racer.motherName}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'motherName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.motherName
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required={isSelfProfile}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lakcím{isSelfProfile && ' *'}
            </label>
            <input
              type="text"
              value={racer.address}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'address', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.address
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required={isSelfProfile}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefonszám{isSelfProfile && ' *'}
            </label>
            <input
              type="tel"
              value={racer.phoneNumber}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'phoneNumber', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.phoneNumber
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              placeholder="+36 30 123 4567"
              required={isSelfProfile}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Születési hely{isSelfProfile && ' *'}
            </label>
            <input
              type="text"
              value={racer.birthPlace}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'birthPlace', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.birthPlace
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required={isSelfProfile}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Születési idő{isSelfProfile && ' *'}
            </label>
            <input
              type="date"
              value={racer.birthDate}
              onChange={(e) => handleRacerChange(currentRacerIndex, 'birthDate', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md ${isSelfProfile && !racer.birthDate
                ? 'border-red-300'
                : 'border-gray-300'
                }`}
              required={isSelfProfile}
            />
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={goToPreviousRacer}
            disabled={currentRacerIndex === 0}
            className={`px-4 py-2 rounded-md ${currentRacerIndex === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            Előző
          </button>

          <button
            type="button"
            onClick={goToNextRacer}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {currentRacerIndex < itemCount - 1 ? 'Következő' : 'Mentés és befejezés'}
          </button>
        </div>
      </div>
    );
  };

  // Betöltés közben
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-600">Adatok betöltése...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 p-4 rounded-lg">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Vendégek adatai</h2>
        <div className="text-sm text-gray-600">
          {currentRacerIndex + 1} / {itemCount} vendég
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">
          {error}
        </div>
      )}

      <div className="mb-4 flex overflow-x-auto py-2">
        {racers.map((racer, index) => (
          <button
            key={index}
            onClick={() => setCurrentRacerIndex(index)}
            className={`flex-shrink-0 px-3 py-1 mr-2 rounded-full ${index === currentRacerIndex
              ? 'bg-blue-600 text-white'
              : checkProfileComplete(racer, index === 0)
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-200 text-gray-700'
              }`}
          >
            {`${index + 1}. vendég`}
          </button>
        ))}
      </div>

      {renderRacerForm()}

      {/* <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 mr-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Mégsem
        </button>

        <button
          type="button"
          onClick={handleSaveAll}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300"
        >
          {saving ? 'Mentés...' : 'Összes mentése és befejezés'}
        </button>
      </div> */}
    </div>
  );
};

export default RacerRegistration; 